import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types/user.types';
import toast from 'react-hot-toast';

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  // Marketing email consent. Starts UNCHECKED: registering to bid is not opting in.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const generateBidderNumber = (): string => {
    // Generate 4-digit bidder number
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create Firebase auth account
      const credential = await createUserWithEmailAndPassword(auth, email, password);

      // Create basic bidder profile
      const userProfile: User = {
        uid: credential.user.uid,
        email: credential.user.email!,
        role: 'bidder', // Default all signups to bidder
        displayName: displayName,
        bidderNumber: generateBidderNumber(),
        cardAuthorized: false,
        isApproved: true,
        isBanned: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        // Recorded either way, so 'said no' is distinguishable from 'was never asked'.
        marketingOptIn,
        marketingOptInSource: 'signup',
        ...(marketingOptIn ? { marketingOptInAt: new Date() } : {}),
      };

      await setDoc(doc(db, 'users', credential.user.uid), userProfile);

      toast.success('Account created! Please complete your bidder profile.');
      navigate('/register-bidder'); // Redirect to complete profile
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Email already in use');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password should be at least 6 characters');
      } else {
        toast.error('Failed to create account');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="card max-w-md w-full">
        <h1 className="text-2xl font-bold mb-2 text-center">
          Create Bidder Account
        </h1>
        <p className="text-sm text-gray-600 text-center mb-6">
          Start bidding on auctions today
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Full Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input"
              placeholder="John Doe"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
              required
              disabled={loading}
            />
          </div>
          
          <div>
            <label className="label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              minLength={6}
              required
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">At least 6 characters</p>
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
              className="mt-1"
              disabled={loading}
            />
            <span>
              Email me about upcoming sales and news. Optional — you can unsubscribe at any time.
            </span>
          </label>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs text-blue-800">
              After creating your account, you'll complete your bidder profile with address and payment information.
            </p>
          </div>
          
          <button 
            type="submit" 
            className="btn-primary w-full"
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create Bidder Account'}
          </button>
        </form>
        
        <p className="text-center mt-4 text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold">
            Sign in
          </Link>
        </p>

        <div className="mt-6 pt-6 border-t">
          <p className="text-xs text-gray-500 text-center">
            Are you an auction house?{' '}
            <Link to="/contact" className="text-primary-600 hover:text-primary-700">
              Contact us
            </Link>
            {' '}about seller accounts
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;